import { useState } from "react";
import KitsByPractice from "./-components/kits-by-practice";
import KitsByLogType from "./-components/kits-by-log-type";
import CostPerKitOverTime from "./-components/cost-per-kit-over-time";
import MetricCards from "./-components/metric-cards";
import { AssistantFilterBar } from "@/components/universal/filter-assistant/filter-bar";
import {
  dateRangeWithGroupingField,
  entityLookupField,
  timePeriodField,
} from "@/components/universal/filter-assistant/presets";
import { Plus } from "lucide-react";
import {
  ParamDateFilter,
  dataTableSearchParsers,
  ParamDataTableToolbar,
} from "@/components/ui/param-data-table";
import { createFileRoute } from "@tanstack/react-router";
import { parseAsString, parseAsStringEnum, useQueryStates } from "nuqs";
import { keepPreviousData } from "@tanstack/react-query";
import {
  GetWarehouseKpisParams,
  TimePeriodEnum,
} from "@/requests/gen/react-query/fastAPI.schemas";
import { useGetEntitiesInfinite } from "@/requests/gen/react-query/entity";
import { Button } from "@/components/ui/button";
import TimePeriodDropdown from "@/components/ui/time-period-dropdown";
import WarehouseLaborForm from "./-components/warehouse-labor-form";
import KitsOvertimeGraph from "./-components/kits-over-time";
import { useOnlyEntity } from "@/hooks/use-only-entity";
import { DropdownFilter } from "@/components/ui/dropdown-filter";
import { getAutoTimePeriod } from "@/lib/time-period";
import { useDefaultEntity } from "@/hooks/use-default-entity";
import { useEffectiveDateRange } from "@/hooks/use-effective-date-range";
import { getThisMonth } from "@/lib/get-date-ranges";

export const Route = createFileRoute(
  "/_private/_warehouse-360/_analytics/warehouse360-analytics/",
)({
  component: WarehouseDashboard,
});

function WarehouseDashboard() {
  const [queryStates, setQueryStates] = useQueryStates({
    ...dataTableSearchParsers,
    date_start_start: parseAsString,
    date_start_end: parseAsString,
    time_period: parseAsStringEnum(Object.values(TimePeriodEnum)),
  });

  const onlyEntity = useOnlyEntity();
  const [fulfillerSearch, setFulfillerSearch] = useState<string | undefined>();

  const {
    effectiveDateStart: effectiveDateStartIso,
    effectiveDateEnd: effectiveDateEndIso,
    effectiveTimePeriod,
    markDateFilterTouched,
  } = useEffectiveDateRange({
    dateStart: queryStates.date_start_start,
    dateEnd: queryStates.date_start_end,
    timePeriod: queryStates.time_period,
  });
  const effectiveDateStart = effectiveDateStartIso?.split("T")[0];
  const effectiveDateEnd = effectiveDateEndIso?.split("T")[0];

  const fulfillerIds = onlyEntity ? [onlyEntity] : queryStates.entity_ids;

  const fulfillers = useGetEntitiesInfinite(
    {
      search: fulfillerSearch,
      include_ids: fulfillerIds,
      entity_types: ["Fulfiller"],
      services: ["warehouse_360"],
    },
    {
      query: {
        placeholderData: keepPreviousData,
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.items.length === 0) return undefined;
          return allPages.length + 1;
        },
      },
    },
  );

  const params: GetWarehouseKpisParams = {
    entity_ids: fulfillerIds,
    date_start: effectiveDateStart,
    date_end: effectiveDateEnd,
    time_period: effectiveTimePeriod,
  };

  useDefaultEntity({
    entities: fulfillers.data?.pages.flatMap((p) => p.items),
    selected: queryStates.entity_ids,
    setSelected: (ids) => setQueryStates({ entity_ids: ids }),
    disabled: !!onlyEntity,
    getEntityId: (fulfiller) => fulfiller.entity_id,
  });

  return (
    <div>
      <title>Warehouse Analytics - Legrande Health Portal</title>
      <AssistantFilterBar
        fields={[
          dateRangeWithGroupingField({
            name: "date_range",
            description: "The date range of warehouse activity to show.",
            current: {
              start: effectiveDateStart,
              end: effectiveDateEnd,
            },
            onApply: ({ start, end }, autoTimePeriod) =>
              setQueryStates({
                date_start_start: start,
                date_start_end: end,
                time_period: autoTimePeriod,
              }),
          }),
          timePeriodField({
            current: effectiveTimePeriod,
            onApply: (time_period) => setQueryStates({ time_period }),
          }),
          ...(onlyEntity
            ? []
            : [
                entityLookupField({
                  name: "entity_ids",
                  entityType: "Fulfiller",
                  summary: "The fulfiller(s) whose warehouse activity is shown",
                  currentIds: queryStates.entity_ids,
                  getLabel: (id) =>
                    fulfillers.data?.pages
                      .flatMap((p) => p.items)
                      .find((f) => f.entity_id === id)?.name,
                  onApply: (ids) => setQueryStates({ entity_ids: ids }),
                }),
              ]),
        ]}
        actions={
          <WarehouseLaborForm>
            <Button size="sm" className="shrink-0">
              <Plus />
              Add Labor Costs
            </Button>
          </WarehouseLaborForm>
        }
      >
        <ParamDataTableToolbar alwaysVisibleCount={0} overflowCount={Infinity}>
          <ParamDateFilter
            title="Date Range"
            name="date_start"
            defaultValue={getThisMonth()}
            onDateChange={(dateRange) => {
              markDateFilterTouched();
              if (dateRange?.from && dateRange?.to) {
                setQueryStates({
                  time_period: getAutoTimePeriod(dateRange.from, dateRange.to),
                });
              }
            }}
          />
          {!onlyEntity && (
            <DropdownFilter
              title="Fulfiller"
              options={[
                {
                  items:
                    fulfillers.data?.pages
                      .flatMap((page) => page.items)
                      .map((fulfiller) => ({
                        label: fulfiller.name,
                        value: fulfiller.entity_id,
                      })) ?? [],
                },
              ]}
              values={queryStates.entity_ids}
              onChange={(values) => setQueryStates({ entity_ids: values })}
              search={fulfillerSearch}
              setSearch={setFulfillerSearch}
              isFetching={fulfillers.isFetchingNextPage}
              fetchNextPage={fulfillers.fetchNextPage}
            />
          )}
          <TimePeriodDropdown
            value={effectiveTimePeriod}
            onChange={(time_period) => setQueryStates({ time_period })}
          />
        </ParamDataTableToolbar>
      </AssistantFilterBar>
      <div className="grid mb-4 grid-cols-2 gap-2 sm:grid-cols-5 sm:gap-4">
        <MetricCards params={params} />
      </div>
      <div className="space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <KitsOvertimeGraph params={params} />
          <KitsByLogType params={params} />
        </div>
        <KitsByPractice params={params} />
        <CostPerKitOverTime params={params} />
      </div>
    </div>
  );
}
